// ------------------------------
// components/holdInvoices.tsx
// Receive -> Hold Invoices: the dashboard for this vault's hold invoices.
// zz-support-hold-invoices-fable.txt 10.3
// ------------------------------

import { observer } from "mobx-react";
import React from "react";
import {
  Alert,
  Button,
  Card,
  Center,
  Group,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconHourglass, IconPlus, IconInfoCircle } from "@tabler/icons-react";
import { BobsWorkspace } from "../stores/BobsWorkspace";
import { BOB_MODALS } from "../helpers/constants";
import {
  formatDurationInWords,
  numberInWordsAndDigits,
} from "../helpers/holdInvoices";
import {
  HoldDecisionCard,
  HoldOpenCard,
  HoldRecentRow,
  holdGlass,
} from "./details/holdInvoiceCard";

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Text fz="xs" fw={700} c="gray.4" tt="uppercase" mt="sm">
    {children}
  </Text>
);

const HoldInvoices = observer(
  ({ bobsWorkspace }: { bobsWorkspace: BobsWorkspace }) => {
    const bob = bobsWorkspace.selectedBob;
    const settings = bobsWorkspace.holdInvoiceSettings?.data;

    // the footer counts are account-wide, matching the caps (D1)
    const inFlightAccount =
      bobsWorkspace.holdOpenCountAccount + bobsWorkspace.holdAcceptedCountAccount;
    const inFlightHere = bob.openHoldInvoices.length + bob.acceptedHoldInvoices.length;
    const inOtherVaults = Math.max(0, inFlightAccount - inFlightHere);
    const isEmpty =
      bob.acceptedHoldInvoices.length === 0 &&
      bob.openHoldInvoices.length === 0 &&
      bob.recentHoldInvoices.length === 0;

    return (
      <Stack px="md" pt="md" gap="md">
        <Center>
          <Group gap="xs">
            <IconHourglass size={28} />
            <Title order={3}>Hold Invoices</Title>
          </Group>
        </Center>

        {settings && (
          <Alert
            variant="light"
            color="blue"
            icon={<IconInfoCircle size={18} />}
            styles={{ root: { background: "rgba(34, 139, 230, 0.08)" } }}
          >
            A hold invoice lets you see a payment before you accept it. When
            you receive a payment you have up to{" "}
            {formatDurationInWords(settings.decision_window_seconds)} to
            accept or reject it. If you do nothing we reject it for you. You
            can have at most{" "}
            {numberInWordsAndDigits(settings.max_in_flight_per_account)} hold
            invoices open at one time.
          </Alert>
        )}

        {bobsWorkspace.accountHoldInvoicesRefusalReason && (
          <Alert variant="light" color="orange">
            {bobsWorkspace.accountHoldInvoicesRefusalReason}
          </Alert>
        )}

        <Group justify="center">
          <Button
            leftSection={<IconPlus size={18} />}
            disabled={!bobsWorkspace.canCreateHoldInvoices}
            onClick={() => {
              bob.setCreatedHoldInvoicePayRequest(null);
              bob.setHoldCreateError(null);
              bob.setOpenModal(BOB_MODALS.CREATE_HOLD_INVOICE);
            }}
          >
            Create hold invoice
          </Button>
        </Group>

        {bob.acceptedHoldInvoices.length > 0 && (
          <>
            <SectionTitle>Needs your decision</SectionTitle>
            {bob.acceptedHoldInvoices.map((hold) => (
              <HoldDecisionCard key={hold.uuid} bob={bob} hold={hold} />
            ))}
          </>
        )}

        {bob.openHoldInvoices.length > 0 && (
          <>
            <SectionTitle>Waiting for payer</SectionTitle>
            {bob.openHoldInvoices.map((hold) => (
              <HoldOpenCard key={hold.uuid} bob={bob} hold={hold} />
            ))}
          </>
        )}

        {bob.recentHoldInvoices.length > 0 && (
          <>
            <SectionTitle>Recent</SectionTitle>
            <Card p={0} style={holdGlass}>
              {bob.recentHoldInvoices.map((hold) => (
                <HoldRecentRow key={hold.uuid} bob={bob} hold={hold} />
              ))}
            </Card>
          </>
        )}

        {isEmpty && (
          <Text ta="center" c="dimmed" fz="sm" py="lg">
            No hold invoices in this vault yet.
          </Text>
        )}

        {settings && (
          <Text ta="center" c="dimmed" fz="xs" pb="md">
            {inFlightAccount} of {settings.max_in_flight_per_account} hold
            invoices open · {bobsWorkspace.holdAcceptedCountAccount} of{" "}
            {settings.max_accepted_per_account} payments awaiting decision
            {inOtherVaults > 0 && ` (includes ${inOtherVaults} in your other vaults)`}
          </Text>
        )}
      </Stack>
    );
  },
);

export default HoldInvoices;
